/*
 * -----------------------------------------------------------------------
 *  <copyright file="subject-monitoring.ts" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { gql, TypedDocumentNode } from '@apollo/client';
import {
    getDataListingsFragment,
    getSubjectMonitoringConfigFragment, PanelListFragment,
    subjectProfileDetailsConfigurationFragment,
} from '@queries/fragments';
import {
    subjectProfileDetailsConfiguration,
    subjectProfileDetailsConfigurationVariables,
    upsertSubjectProfileDetailsConfiguration,
    upsertSubjectProfileDetailsConfigurationVariables,
    getSmPanels,
    getSmPanelsVariables,
} from '@queries/generated/subject-monitoring.generated';

export const GET_SUBJECT_MONITORING_CONFIGURATION = gql`
    query getSubjectMonitoringConfig($studyId: ObjectId!) {
        dataListingsConfigTableForStudy(studyId: $studyId) {
            ...SubjectMonitoringConfigFields
        }
    }
    ${getSubjectMonitoringConfigFragment}
`;

export const UPDATE_SUBJECT_MONITORING_CONFIGURATION = gql`
    mutation updateSubjectMonitoringConfig($studyId: ObjectId!, $fields: [DataListingsListItemInput]!) {
        upsertDataListingsTable(studyId: $studyId, fields: $fields)
    }
`;

export const GET_DATA_LISTINGS_TABLES_FOR_STUDY = gql`
    query dataListingsTablesForStudy($studyId: ObjectId!, $timestamp: DateTime) {
        dataListingsForStudy(studyId: $studyId, timestamp: $timestamp) {
            ...DataListingsFields
        }
    }
    ${getDataListingsFragment}
`;

export const GET_SUBJECT_MONITORING_CONFIGURATION_FOR_TIMESTAMP = gql`
    query dataListingsConfigTableForStudy($studyId: ObjectId!, $timestamp: DateTime!) {
        dataListingsConfigTableForStudy(studyId: $studyId, timestamp: $timestamp) {
            ...SubjectMonitoringConfigFields
        }
    }
    ${getSubjectMonitoringConfigFragment}
`;

export const GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION: TypedDocumentNode<
    subjectProfileDetailsConfiguration,
    subjectProfileDetailsConfigurationVariables
> = gql`
    query subjectProfileDetailsConfiguration($studyId: ObjectId!, $timestamp: DateTime) {
        subjectProfileDetailsConfiguration(studyId: $studyId, timestamp: $timestamp) {
            ...SubjectProfileDetailsConfigurationFields
        }
    }
    ${subjectProfileDetailsConfigurationFragment}
`;

export const UPSERT_SUBJECT_PROFILE_DETAILS_CONFIGURATION: TypedDocumentNode<
    upsertSubjectProfileDetailsConfiguration,
    upsertSubjectProfileDetailsConfigurationVariables
> = gql`
    mutation upsertSubjectProfileDetailsConfiguration($studyId: ObjectId!, $configItem: SubjectProfileDetailsConfigurationItemInput!) {
        upsertSubjectProfileDetailsConfiguration(studyId: $studyId, config: $configItem) {
            ...SubjectProfileDetailsConfigurationFields
        }
    }
    ${subjectProfileDetailsConfigurationFragment}
`;

export const GET_SM_PANELS: TypedDocumentNode<getSmPanels, getSmPanelsVariables> = gql`
    query getSmPanels($organisationId: ObjectId!, $studyId: ObjectId!, $timestamp: DateTime) {
        smPanels(organisationId: $organisationId, studyId: $studyId, timestamp: $timestamp) {
            ...PanelListFields
        }
    }
    ${PanelListFragment}
`;